// Shared domain types for Syrinx. Kept dependency-free so both the app and the
// build-time pipeline can import them.

/** The three learning skills. One FSRS card exists per species × skill. */
export type Skill = 'sound' | 'names' | 'photo';

export const SKILLS: readonly Skill[] = ['sound', 'names', 'photo'] as const;

/** Xeno-canto recording type, normalized to the categories the UI filters on. */
export type SoundType = 'song' | 'call' | 'alarm';

/** A Creative Commons license code, as stored per asset for attribution. */
export type LicenseCode =
  | 'CC0'
  | 'CC-BY'
  | 'CC-BY-SA'
  | 'CC-BY-NC'
  | 'CC-BY-NC-SA';

/** Attribution metadata carried by every bundled media asset. */
export interface Attribution {
  license: LicenseCode;
  author: string;
  /** Canonical page for the source recording/photo. */
  sourceUrl: string;
  /** Provider the asset came from. */
  source: 'xeno-canto' | 'inaturalist' | 'wikimedia';
}

export interface AudioAsset extends Attribution {
  id: string;
  type: SoundType;
  /** Path within the pack, e.g. "pnw/audio/xc123.opus". */
  file: string;
  /**
   * Precomputed waveform, inline: 48 bytes of 0..255 amplitude, base64'd.
   *
   * Inline rather than a pack path because these were a separate HTTP request
   * per clip — the single largest contributor to pack request count, for ~48
   * bytes of real data each. Riding in the manifest also means waveforms render
   * before the media pack has been downloaded at all.
   */
  peaks: string;
  /** Duration in seconds. */
  dur: number;
  bytes: number;
}

export interface PhotoAsset extends Attribution {
  id: string;
  /** Card-sized image path (~480px). */
  file: string;
  /** Thumbnail path (~160px). */
  thumb: string;
  /** Size of `file`. */
  bytes: number;
  /**
   * Size of `thumb`. Optional only because manifests predate it — without it a
   * pack's advertised download size is short by every thumbnail in it, which at
   * 120 species is ~2 MB of the ~28 MB total.
   */
  thumbBytes?: number;
}

/**
 * Distractor pools, split by what the learner is actually comparing.
 *
 * One flat list served every quiz until this split, and it was scored mostly on
 * name similarity — right for Name Match, quietly wrong for Sound ID, where it
 * offered birds that share a name and nothing else ("Golden-crowned Kinglet"
 * against "Golden-crowned Sparrow") while missing the pairs that actually fool
 * an ear in the field.
 */
export interface Confusables {
  /** Sound-alikes: taxonomy plus the hand-authored acoustic layer. */
  sound: string[];
  /** Name- and look-alikes: taxonomy plus name-string similarity. */
  name: string[];
}

/**
 * Which list a skill draws from. Photo ID reads the `name` list because that's
 * the taxonomy-weighted one, and taxonomy is the only signal we have that
 * tracks appearance — same genus mostly means same silhouette and plumage
 * pattern. The `sound` list is explicitly the wrong one for it: half the point
 * of the acoustic layer is pairs that look nothing alike (Black-headed Grosbeak
 * against American Robin).
 */
const CONFUSABLE_LIST: Record<Skill, keyof Confusables> = {
  sound: 'sound',
  names: 'name',
  photo: 'name',
};

/**
 * The distractor cluster for a species in a given skill, tolerant of manifests
 * written before the split.
 *
 * The legacy branch is load-bearing rather than tidy: the service worker
 * precaches region.json with the app shell, so a client can genuinely be
 * running new code against a manifest that still carries a flat array. Reading
 * it as both lists degrades to the old behaviour instead of throwing.
 */
export function confusablesFor(sp: Species | undefined, skill: Skill): string[] {
  const c = sp?.confusable as Confusables | string[] | undefined;
  if (!c) return [];
  if (Array.isArray(c)) return c;
  return c[CONFUSABLE_LIST[skill]] ?? [];
}

/**
 * What the two halves of a binomial mean, in plain English.
 *
 * Two glosses rather than one sentence because a binomial is two words doing
 * two jobs — "Spatula cyanoptera" is a spoon-billed duck with blue wings — and
 * the quiz sets them under the name they belong to. Written at build time by
 * `tools/pipeline/lib/etymology.ts`; absent for a species nobody has run it for
 * yet, which every reader has to treat as normal rather than as missing data.
 */
export interface Etymology {
  /** What the genus name means: "Spatula" → "spoon". */
  genus: string;
  /** What the specific epithet means: "cyanoptera" → "blue-winged". */
  epithet: string;
}

/**
 * The five axes a bird is described on beyond its name: how long it is, what it
 * eats, where it lives, what its feet are for, what it nests in.
 *
 * The vocabularies, the labels and the validity check live in
 * `src/core/traits.ts`; only the shape is here, because only the shape belongs to
 * the manifest. Hand-written per species in `tools/pipeline/data/traits.json` and
 * joined in by stage 06, the way `etym` is.
 */
export type TraitKey = 'size' | 'prey' | 'habitat' | 'feet' | 'nest';

/**
 * Every axis is a list, `size` included — a bird eats several things, lives in
 * several kinds of country, and can straddle two length buckets. See the header
 * of `src/core/traits.ts` for why that is one rule and not five decisions.
 */
export type SpeciesTraits = Record<TraitKey, string[]>;

/** Per-species record within a region's species.json manifest. */
export interface Species {
  /** eBird species code, stable id used as the primary key everywhere. */
  code: string;
  /** Scientific (Latin) binomial. */
  sci: string;
  /** English common name. */
  common: string;
  order?: string;
  family?: string;
  /**
   * How commonly this species is seen in the region, 1 = most common.
   * Drives the order new cards are introduced in — deliberately not the
   * manifest's own order, which is taxonomic (see `studyQueue`).
   */
  rank?: number;
  /** Species codes easily confused with this one, per quiz type. */
  confusable: Confusables;
  /** What the binomial means — shown once a name question has been answered. */
  etym?: Etymology;
  /**
   * What the bird is: size, prey, habitat, feet, nest. Optional for `etym`'s
   * reason — the service worker precaches `region.json` with the app shell, so
   * new code can be running against a manifest written before this existed. Read
   * it through `traitsOf`/`hasTraits` rather than directly.
   */
  traits?: SpeciesTraits;
  /**
   * The day this species joined the pack, ISO `yyyy-mm-dd`. Stamped by the
   * pipeline when a species is added and joined in by stage 06, the way `etym`
   * and `traits` are.
   *
   * Optional for two reasons, both of which a reader has to treat as ordinary:
   * the 120 species added before the field existed carry none and never will,
   * and a precached `region.json` can be older than the code reading it. Read it
   * through `newestBatch` in core/region.ts rather than directly.
   */
  addedAt?: string;
  audio: AudioAsset[];
  photos: PhotoAsset[];
}

/** Small region descriptor committed to the app (metadata only, no binaries). */
export interface RegionManifest {
  id: string;
  label: string;
  /** Content version, e.g. "2026.07" — drives pack cache-bucket naming. */
  version: string;
  /** Base URL the media pack is served from (may be overridden at runtime). */
  packBaseUrl: string;
  speciesCount: number;
  /** Species ordered for display (taxonomic order). */
  species: Species[];
}

/** Composite key for an FSRS card: `${speciesCode}:${skill}`. */
export type CardKey = `${string}:${Skill}`;

export function cardKey(code: string, skill: Skill): CardKey {
  return `${code}:${skill}`;
}
